import { ComponentProps, ReactNode } from 'react'
import type { RenderableFilterOption, SelectedValues } from 'shared/types/util'
import { useDispatch } from 'react-redux'
import { BasicCollapsible, SelectionSet, SelectionSetInput, SelectionSetLabel } from '@vp/swan'

import { useFilterOptions } from '~/app/features/Gallery/hooks/useFilterOptions'
import { buildRefinement, refinementAddAndRemove } from '~/app/store/refinement'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { scrollUpToElement } from '~/app/utils/scrollToElement'
import { galleryHeaderId } from '~/app/features/Gallery/components/Header/ActionHeader/constants'
import { isFilterModalHidden } from '~/app/features/Gallery/components/Header/ActionHeader/components/FilterMenu/utils'
import { useRedirectToL0 } from '~/app/features/Gallery/hooks/RedirectToL0/useRedirectToL0'
import { ThunkDispatch } from '~/app/store/interface'
import type { State } from 'shared/types/gallery/state'

interface PropTypes {
  children: (o: RenderableFilterOption) => ReactNode
  CollapsibleProps?: ComponentProps<typeof BasicCollapsible>
  filter: State.AttributeFilter
  SelectionSetProps?: Omit<
    ComponentProps<typeof SelectionSet>,
    'defaultSelectedValue' | 'variant' | 'selectedValue' | 'selectedValues' | 'onSelectedValueChange' | 'onSelectedValuesChange' | 'skin'
  >
}

export const MultiSelectFilter = (props: PropTypes) => {
  const { children, CollapsibleProps = {}, filter, SelectionSetProps = {} } = props
  const { options, selected, visible } = useFilterOptions(filter)
  const dispatch = useDispatch<ThunkDispatch>()
  const redirectToL0 = useRedirectToL0()

  const handleSelectedValuesChange = (newSelectedValues: SelectedValues): void => {
    const toAdd: State.Refinement[] = []
    const toRemove: State.Refinement[] = []

    for (const [value, isSelected] of Object.entries(newSelectedValues)) {
      const refinement = buildRefinement(value, REFINEMENT_DIMENSION.ATTRIBUTE)

      ;(isSelected ? toAdd : toRemove).push(refinement)
    }

    if (redirectToL0(toAdd, toRemove)) {
      return
    }

    dispatch(refinementAddAndRemove(toAdd, toRemove))
    if (isFilterModalHidden()) {
      scrollUpToElement(galleryHeaderId)
    }
  }

  return (
    <>
      {!visible && null}
      {visible && (
        <BasicCollapsible
          aria-label={filter.title}
          className="filter"
          collapsibleId={filter.name}
          heading={filter.title}
          headingLevel="h4"
          {...CollapsibleProps}
        >
          <SelectionSet
            aria-label={filter.title}
            className="selection-set-layout-override"
            selectedValues={selected}
            skin="simple-column"
            variant="multi-select"
            onSelectedValuesChange={handleSelectedValuesChange}
            {...SelectionSetProps}
          >
            {options.map((o) => (
              <div className="selection-set-item" data-testid="filter-value" key={o.value}>
                <SelectionSetInput disabled={o.disabled} value={o.value}>
                  <SelectionSetLabel>{children(o)}</SelectionSetLabel>
                </SelectionSetInput>
              </div>
            ))}
          </SelectionSet>
        </BasicCollapsible>
      )}
    </>
  )
}

MultiSelectFilter.displayName = 'MultiSelectFilter'
